/**
 * @fileoverview `drawLabel` / `removeLabel`: labels as an HTML overlay on the canvas.
 *
 * Why not PyMOL labels on pseudoatoms (they do render on this port, pymol-web first-frame.md):
 *   - 3Dmol's labels are screen-space sprites, drawn in front of everything and NOT clickable; a
 *     PyMOL label is pickable and would swallow the click on the hotspot atom behind it;
 *   - BMaps' LabelSpec (18 px sans-serif, rounded black box, 4 px padding, `alignment`,
 *     `backgroundOpacity`, `fontColor` ...) maps one to one onto CSS; PyMOL's label settings are
 *     another model (points, `label_position` offsets, square boxes) and would need a separate
 *     `label_size` per device pixel ratio to stay legible;
 *   - the text is crisp at DPR 1 and 2 because the browser draws it.
 * The price: the overlay is not in the canvas pixels, so GetImageBlob() paints the labels onto
 * the captured frame itself (`composite`) - 3Dmol's export includes its labels.
 *
 * Positions are 3Dmol's: the centroid of the label's atoms, fixed when the label is drawn
 * (GLViewer.addLabel). They are re-projected after every frame PyMOL draws with another view or
 * viewport size (the wrapper's `frame` event), and after each flush.
 */

import { LabelDefaults, colorToInt } from './PyMOLConstants';
import { sameView } from './PyMOLProjection';

/** 3Dmol SpriteAlignment -> where the anchor sits on the label box (fractions of its size). */
const Alignments = {
    topLeft: [0, 0],
    topCenter: [-0.5, 0],
    topRight: [-1, 0],
    centerLeft: [0, -0.5],
    center: [-0.5, -0.5],
    centerRight: [-1, -0.5],
    bottomLeft: [0, -1],
    bottomCenter: [-0.5, -1],
    bottomRight: [-1, -1],
};

function isFalseFlag(value) {
    return value === false || value === 'false' || value === '0' || value === 0;
}

/** 'rgba(r,g,b,a)' -> {hex: '0xRRGGBB', alpha} (null for anything else) */
function parseRgba(text) {
    const m = /^rgba\((\d+),(\d+),(\d+),([\d.]+)\)$/.exec(String(text || '').replace(/\s+/g, ''));
    if (!m) return null;
    const hex = [m[1], m[2], m[3]].map((v) => Number(v).toString(16).padStart(2, '0')).join('');
    return { hex: `0x${hex}`, alpha: Number(m[4]) };
}

/**
 * PyMOL's label settings for a label's look (labelStyle), for the session export: text and
 * background color per atom (atom-state settings; colors as "0xRRGGBB" STRINGS - an int is a
 * color index), the rest per object (label_size and label_font_id are object-state).
 * @returns {{atom: Object<string, *>, size: number, bold: boolean}}
 */
export function labelExportSettings(style) {
    const atom = {};
    const color = parseRgba(style && style.color);
    if (color) atom.label_color = color.hex;
    const background = parseRgba(style && style.background);
    if (background) {
        atom.label_bg_color = background.hex;
        atom.label_bg_transparency = Number((1 - background.alpha).toFixed(2));
    }
    return {
        atom,
        size: (style && style.fontSize) || LabelDefaults.fontSize,
        bold: /^bold /.test((style && style.font) || ''),
    };
}

/** 3Dmol getColor(color, opacity, default) -> 'rgba(...)' */
function rgba(color, opacity, fallback) {
    let value = colorToInt(color);
    if (value == null) value = fallback;
    const valid = opacity != null && Number.isFinite(Number(opacity));
    const a = valid ? Math.max(0, Math.min(1, Number(opacity))) : 1;
    /* eslint-disable no-bitwise */
    return `rgba(${(value >> 16) & 0xff},${(value >> 8) & 0xff},${value & 0xff},${a})`;
    /* eslint-enable no-bitwise */
}

/**
 * The resolved look of a label (pure; used for the DOM element and for the export).
 * @param {object} [options] a 3Dmol LabelSpec as BMaps passes it
 */
export function labelStyle(options={}) {
    const showBackground = !isFalseFlag(options.showBackground);
    const fontSize = parseInt(options.fontSize, 10) || LabelDefaults.fontSize;
    const background = rgba(options.backgroundColor, options.backgroundOpacity,
        LabelDefaults.backgroundColor);
    const border = showBackground ? (Number(options.borderThickness) || 0) : 0;
    const alignment = Alignments[options.alignment] ? options.alignment : LabelDefaults.alignment;
    const offset = options.screenOffset || {};
    return {
        font: `${options.bold ? 'bold ' : ''}${fontSize}px ${options.font || LabelDefaults.font}`,
        fontSize,
        color: rgba(options.fontColor, options.fontOpacity, LabelDefaults.fontColor),
        background: showBackground ? background : null,
        border,
        borderColor: options.borderColor != null
            ? rgba(options.borderColor, options.borderOpacity, LabelDefaults.backgroundColor)
            : background,
        padding: options.padding != null && Number.isFinite(Number(options.padding))
            ? Number(options.padding) : LabelDefaults.padding,
        align: Alignments[alignment],
        offset: [Number(offset.x) || 0, Number(offset.y) || 0],
    };
}

export class PyMOLLabels {
    constructor() {
        /** @type {Map<object, {handle, anchor: number[]|null, style: object, element}>} */
        this.labels = new Map();
        this.layer = null;
        this.canvas = null;
        this.lastKey = null;
        /** CSS positions of the last update, for the export: handle -> {x, y} | null */
        this.positions = new Map();
    }

    get size() { return this.labels.size; }

    /**
     * What each label says and where it sits in model coordinates - for putting the overlay into
     * something that leaves the browser (a session file), where an HTML layer means nothing.
     * @returns {{text: string, anchor: number[]}[]}
     */
    records() {
        return [...this.labels.values()]
            .filter((r) => Array.isArray(r.anchor) && r.anchor.length === 3)
            .map((r) => ({
                text: String(r.handle.text != null ? r.handle.text : ''),
                anchor: r.anchor,
                style: r.style,
            }));
    }

    /** The canvas the overlay sits on (its parent gets the layer, right after the canvas). */
    attachCanvas(canvas) { this.canvas = canvas; }

    /**
     * @param {object} handle what drawLabel returned
     * @param {number[]|null} anchor model coordinates, null = nowhere to draw it
     */
    add(handle, anchor) {
        const style = labelStyle(handle.options || {});
        const record = {
            handle, anchor, style, element: null,
        };
        this.labels.set(handle, record);
        record.element = this.makeElement(handle.text, style);
        this.lastKey = null;
        return record;
    }

    remove(handle) {
        const record = handle && this.labels.get(handle);
        if (!record) return false;
        this.labels.delete(handle);
        this.positions.delete(handle);
        if (record.element && typeof record.element.remove === 'function') record.element.remove();
        return true;
    }

    clear() {
        for (const handle of [...this.labels.keys()]) this.remove(handle);
    }

    dispose() {
        this.clear();
        if (this.layer && typeof this.layer.remove === 'function') this.layer.remove();
        this.layer = null;
    }

    ensureLayer() {
        if (this.layer) return this.layer;
        const canvas = this.canvas;
        if (!canvas) return null;
        // (`parent` is the test double's name for parentNode)
        const parent = canvas.parent !== undefined ? canvas.parent : canvas.parentNode;
        const doc = canvas && canvas.ownerDocument;
        if (!parent || !doc || typeof doc.createElement !== 'function') return null;
        const layer = doc.createElement('div');
        Object.assign(layer.style, {
            position: 'absolute',
            top: '0px',
            left: '0px',
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            pointerEvents: 'none', // 3Dmol's labels are not clickable: the canvas gets the events
            zIndex: '0', // above the canvas (later sibling), below the app's popups (z-index 5)
        });
        layer.setAttribute('data-role', '3d-labels');
        layer.setAttribute('aria-hidden', 'true');
        if (typeof parent.insertBefore === 'function' && canvas.nextSibling !== undefined) {
            parent.insertBefore(layer, canvas.nextSibling);
        } else {
            parent.appendChild(layer);
        }
        this.layer = layer;
        return layer;
    }

    makeElement(text, style) {
        const layer = this.ensureLayer();
        if (!layer) return null;
        const element = layer.ownerDocument.createElement('div');
        Object.assign(element.style, {
            position: 'absolute',
            left: '0px',
            top: '0px',
            display: 'none',
            font: style.font,
            lineHeight: String(LabelDefaults.lineHeight),
            color: style.color,
            background: style.background || 'transparent',
            padding: `${style.padding}px`,
            border: style.border ? `${style.border}px solid ${style.borderColor}` : 'none',
            borderRadius: style.background ? `${LabelDefaults.borderRadius}px` : '0px',
            whiteSpace: 'pre',
            userSelect: 'none',
            willChange: 'transform',
        });
        element.textContent = String(text == null ? '' : text);
        layer.appendChild(element);
        return element;
    }

    /**
     * Re-project every label. Cheap to call often: nothing happens while view and size stay.
     * @param {{view: number[], width: number, height: number, project: function}|null} projection
     */
    update(projection, force=false) {
        if (!projection || this.labels.size === 0) return;
        const key = [...projection.view, projection.width, projection.height];
        if (!force && this.lastKey && sameView(this.lastKey, key)) return;
        this.lastKey = key;
        for (const record of this.labels.values()) {
            let position = null;
            if (record.anchor) {
                const p = projection.project(...record.anchor);
                if (p.depth > 0 && Number.isFinite(p.x) && Number.isFinite(p.y)) {
                    position = { x: p.x, y: p.y };
                }
            }
            this.positions.set(record.handle, position);
            const { element, style } = record;
            if (!element) continue;
            if (!position) {
                element.style.display = 'none';
                continue;
            }
            const x = position.x + style.offset[0];
            const y = position.y + style.offset[1];
            element.style.display = 'block';
            element.style.transform = `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px) `
                + `translate(${style.align[0] * 100}%, ${style.align[1] * 100}%)`;
        }
    }

    /**
     * Paint the labels onto a captured frame (image export). The overlay is not in the canvas.
     * @param {Blob} blob the frame
     * @param {{cssWidth: number, type?: string, quality?: number}} options
     * @returns {Promise<Blob>}
     */
    async composite(blob, { cssWidth, type, quality }={}) {
        const visible = [...this.labels.values()].filter((r) => this.positions.get(r.handle));
        if (!blob || visible.length === 0 || typeof createImageBitmap !== 'function'
            || typeof document === 'undefined') return blob;
        const bitmap = await createImageBitmap(blob);
        const canvas = document.createElement('canvas');
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(bitmap, 0, 0);
        const s = cssWidth > 0 ? bitmap.width / cssWidth : 1;
        for (const { handle, style } of visible) {
            const { x, y } = this.positions.get(handle);
            paintLabel(ctx, String(handle.text == null ? '' : handle.text), style,
                (x + style.offset[0]) * s, (y + style.offset[1]) * s, s);
        }
        if (typeof bitmap.close === 'function') bitmap.close();
        return new Promise((resolve) => {
            canvas.toBlob((out) => resolve(out || blob), type || blob.type || 'image/png', quality);
        });
    }
}

/** 3Dmol Label.setContext, drawn at scale s with the anchor at (ax, ay). */
function paintLabel(ctx, text, style, ax, ay, s) {
    ctx.font = style.font.replace(/(\d+)px/, (_, px) => `${Number(px) * s}px`);
    const pad = style.padding * s;
    const border = style.border * s;
    const width = ctx.measureText(text).width + 2.5 * border + 2 * pad;
    const height = style.fontSize * LabelDefaults.lineHeight * s + 2 * border + 2 * pad;
    const left = ax + style.align[0] * width;
    const top = ay + style.align[1] * height;
    if (style.background) {
        const r = Math.min(LabelDefaults.borderRadius * s, width / 2, height / 2);
        ctx.beginPath();
        ctx.moveTo(left + r, top);
        ctx.arcTo(left + width, top, left + width, top + height, r);
        ctx.arcTo(left + width, top + height, left, top + height, r);
        ctx.arcTo(left, top + height, left, top, r);
        ctx.arcTo(left, top, left + width, top, r);
        ctx.closePath();
        ctx.fillStyle = style.background;
        ctx.fill();
        if (border > 0) {
            ctx.lineWidth = border;
            ctx.strokeStyle = style.borderColor;
            ctx.stroke();
        }
    }
    ctx.fillStyle = style.color;
    ctx.textBaseline = 'alphabetic';
    ctx.fillText(text, left + border + pad, top + style.fontSize * s + border + pad);
}
